export type FacebookPublishDateTimeState =
  | { status: 'not_scheduled'; date: null }
  | { status: 'scheduled'; date: Date }
  | { status: 'published'; date: Date | null }
  | { status: 'failed'; date: Date | null };

type SchedulableFacebookFields = {
  facebookBlogAutoPostId?: string | null;
  facebookBlogAutoPostError?: string | null;
  socialMediaLinkedPostId?: string | null;
  socialMediaPostingEnabled?: boolean | null;
  socialMediaPostingScheduledAt?: Date | string | null;
  socialMediaPostingPublishedAt?: Date | string | null;
};

function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}

export function parseSocialMediaPostingScheduledAt(raw: unknown): Date | null {
  if (raw == null) return null;
  if (raw instanceof Date) return isValidDate(raw) ? raw : null;

  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  const value = String(raw).trim();
  if (!value) return null;

  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
}

export function toDatetimeLocalValue(raw: Date | string | null | undefined): string {
  const date = parseSocialMediaPostingScheduledAt(raw);
  if (!date) return '';

  const pad = (value: number) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
}

export function datetimeLocalToIsoOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  return isValidDate(date) ? date.toISOString() : null;
}

export function resolveBlogFacebookPublishDateTimeState(
  doc: SchedulableFacebookFields
): FacebookPublishDateTimeState {
  const publishedAt = parseSocialMediaPostingScheduledAt(doc.socialMediaPostingPublishedAt);
  if (publishedAt) {
    return { status: 'published', date: publishedAt };
  }

  const hasFacebookLink = !!(
    doc.facebookBlogAutoPostId?.trim() || doc.socialMediaLinkedPostId?.trim()
  );
  if (hasFacebookLink) {
    return { status: 'published', date: null };
  }

  const scheduledAt = parseSocialMediaPostingScheduledAt(doc.socialMediaPostingScheduledAt);
  if (doc.facebookBlogAutoPostError?.trim()) {
    return { status: 'failed', date: scheduledAt };
  }

  if (doc.socialMediaPostingEnabled === true && scheduledAt) {
    return { status: 'scheduled', date: scheduledAt };
  }

  return { status: 'not_scheduled', date: null };
}

export const resolveFacebookPublishDateTimeState = resolveBlogFacebookPublishDateTimeState;

export function canEditFacebookPublishSchedule(doc: SchedulableFacebookFields): boolean {
  return resolveFacebookPublishDateTimeState(doc).status !== 'published';
}
